import NewDashboard from '@/components/dashboard/layouts/NewDashboard';
import OldDashboard from '@/components/dashboard/layouts/OldDashboard';
import MetaHead from '@/components/util/MetaHead';
import {
  isHpe,
  isOldDashboardDefault,
  isWorkingLocationsEnabled
} from '@/lib/featureFlags';
import { useRouter } from 'next/router';
import V2Dashboard from '@/components/dashboard/layouts/V2Dashboard';
import { getLayout } from '@/components/layouts/AppLayout/AppLayout';
import { useAuthenticated } from '@gettactic/hooks';
import { withTranslationProps } from '@webV2/helpers/locale/withTranslationProps';
import { DashboardStateProvider } from '@/contexts/DashboardStateContext';
import { useEffect } from 'react';
import { useState } from 'react';
import useInstallTelemetry from '@/hooks/useInstallTelemetry';
import { dashboardStateFallbackValues } from '@/contexts/DashboardStateContext';
import { useIsDesignSystem2 } from '@webV2/helpers/useIsDesignSystem2';

function Dashboard() {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const router = useRouter();

  // There are 4 possible states:
  // 1. The user should only be shown the new dashboard. Either new customer or migrated.
  // 2. The user is NOT migrated and should be shown the old dashboard.
  // 3. The user is NOT migrated BUT there are specific users that have access to the new dashboard.
  // 4. The user is a Tactic employee and should be shown the user's default when impersonating but should have the option to switch.
  const newDashboardAccess = isWorkingLocationsEnabled(authenticatedUser);
  const accessToNewDefaultOld = isOldDashboardDefault(authenticatedUser);

  const isPrivacyEnabled = isHpe(authenticatedUser);

  const isV2 = useIsDesignSystem2();
  const isNewestDesign = isV2;

  useEffect(() => {
    if (!authenticatedUser.organization && authenticatedUser.redirect.url) {
      location.href = authenticatedUser.redirect.url;
    } else if (
      !authenticatedUser.offices?.offices ||
      !authenticatedUser.offices?.offices.length
    ) {
      // Push user back to office setup if no offices exist
      location.href = '/setup/offices';
    }
  }, [authenticatedUser]);

  // TODO: Add theme support
  // const hostname = location?.hostname;
  // useEffect(() => {
  //   if (hostname) {
  //     const organizationSlug = hostname.split('.')[0] ?? authenticatedUser.slug ?? DEFAULT_THEME;
  //     applyTheme(organizationSlug);
  //     setTheme(organizationSlug);
  //   }
  // }, [hostname]);

  return (
    <>
      <MetaHead
        title={`Dashboard | ${authenticatedUser.organization?.name ? authenticatedUser.organization?.name : 'Tactic'}`}
        robots="none"
        pageURL={`https://${authenticatedUser.organization?.slug}.gettactic.com/`}
      />
      {router.query.old && !isPrivacyEnabled ? <OldDashboard /> : null}
      {router.query.new ? (
        <div className="h-full bg-white text-tertiary">
          <NewDashboard />
        </div>
      ) : null}
      {accessToNewDefaultOld ? <OldDashboard /> : null}
      {!newDashboardAccess ? <OldDashboard /> : null}
      {newDashboardAccess ? (
        isNewestDesign ? (
          <div className="isolate w-full flex-1 overflow-hidden">
            <div className="h-full">
              <DashboardLayout />
            </div>
          </div>
        ) : (
          <div className="h-full bg-white">
            <NewDashboard />
          </div>
        )
      ) : null}
    </>
  );
}

const DashboardLayout = () => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  useInstallTelemetry();

  const hideWeekends =
    authenticatedUser.organization?.features?.includes('hide.weekends') ??
    false;

  return (
    <DashboardStateProvider
      hideWeekends={hideWeekends}
      defaultFilters={{
        ...dashboardStateFallbackValues,
        office: currentOffice?.id ?? null
      }}
      selectedEvent={selectedEvent}
      setSelectedEvent={setSelectedEvent}
    >
      <V2Dashboard />
    </DashboardStateProvider>
  );
};

Dashboard.getLayout = getLayout;
Dashboard.extraProps = { hideOfficeMenu: true };

export const getStaticProps = withTranslationProps();
export default Dashboard;
